import React, { useState, useMemo } from 'react';
import { useProject } from '../context/ProjectContext';
import { componentTypes, cableTypes, crossSections, ratedCurrents, faultCurrents, characteristics, poles, gridTypes, phaseCount, getLeiterConfig, terminalTypes, ussTypes, ussBauformen, ussPhasen, ussHersteller, meterMontagearten, meterMessarten, meterArten, meterVerdrahtung } from '../data/manufacturers';
import { products } from '../data/products';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Save, Trash2, Copy, MoveUp, MoveDown, ExternalLink, AlertCircle, Link2, ChevronRight, Zap, Info } from 'lucide-react';

const ComponentEditor = () => {
  const { selectedNode, updateNode, deleteNode, findParentNode, currentProject } = useProject();
  const [zuordnungDialog, setZuordnungDialog] = useState({ open: false, aderIdx: null });

  // Alle Bauteile mit Ein/Ausgängen sammeln
  const getZuordnungTargets = () => {
    const targets = [];
    const collectTargets = (nodes, path = '') => {
      nodes?.forEach(node => {
        const fullPath = path ? `${path} > ${node.bezeichnung}` : node.bezeichnung;
        const type = node.type;
        
        // Bauteile mit Ein/Ausgängen
        if (['schuetz', 'stromstoss', 'relais', 'dimmer', 'homematicAktor', 'taster', 'meldeleuchte'].includes(type)) {
          // Relais/Schütz haben Spule und Schaltkontakte
          if (type === 'schuetz' || type === 'stromstoss' || type === 'relais') {
            targets.push({ id: node.id, label: `${fullPath} - Erregerspule A1`, type: 'spule_a1', bezeichnung: node.bezeichnung });
            targets.push({ id: node.id, label: `${fullPath} - Erregerspule A2`, type: 'spule_a2', bezeichnung: node.bezeichnung });
            targets.push({ id: node.id, label: `${fullPath} - Schaltkontakt Eingang`, type: 'kontakt_ein', bezeichnung: node.bezeichnung });
            targets.push({ id: node.id, label: `${fullPath} - Schaltkontakt Ausgang`, type: 'kontakt_aus', bezeichnung: node.bezeichnung });
          }
          // Dimmer haben Eingang/Ausgang
          if (type === 'dimmer') {
            targets.push({ id: node.id, label: `${fullPath} - Eingang L`, type: 'eingang_l', bezeichnung: node.bezeichnung });
            targets.push({ id: node.id, label: `${fullPath} - Ausgang geschaltet`, type: 'ausgang', bezeichnung: node.bezeichnung });
          }
          // Aktoren haben Kanäle
          if (type === 'homematicAktor') {
            const kanaele = node.data?.kanaele || 1;
            for (let i = 1; i <= kanaele; i++) {
              targets.push({ id: node.id, label: `${fullPath} - Kanal ${i} Eingang`, type: `kanal${i}_ein`, bezeichnung: node.bezeichnung });
              targets.push({ id: node.id, label: `${fullPath} - Kanal ${i} Ausgang`, type: `kanal${i}_aus`, bezeichnung: node.bezeichnung });
            }
          }
          // Taster/Meldeleuchte
          if (type === 'taster' || type === 'meldeleuchte') {
            targets.push({ id: node.id, label: `${fullPath} - Anschluss 1`, type: 'anschluss_1', bezeichnung: node.bezeichnung });
            targets.push({ id: node.id, label: `${fullPath} - Anschluss 2`, type: 'anschluss_2', bezeichnung: node.bezeichnung });
          }
        }
        
        if (node.children) {
          collectTargets(node.children, fullPath);
        }
      });
    };
    collectTargets(currentProject?.nodes);
    return targets;
  };

  const handleZuordnung = (aderIdx, target) => {
    const newAdern = [...(selectedNode.data?.adern || [])];
    newAdern[aderIdx] = { 
      ...newAdern[aderIdx], 
      zuordnung: target ? `${target.bezeichnung}:${target.type}` : 'auto',
      zuordnungId: target?.id || null,
      zuordnungType: target?.type || null
    };
    updateNode(selectedNode.id, { data: { ...selectedNode.data, adern: newAdern } });
    setZuordnungDialog({ open: false, aderIdx: null });
  };

  if (!selectedNode) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center text-gray-400">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg">Keine Komponente ausgewählt</p>
          <p className="text-sm mt-2">Wählen Sie eine Komponente aus der Baumstruktur aus oder erstellen Sie eine neue.</p>
        </div>
      </div>
    );
  }

  const componentType = componentTypes[selectedNode.type];
  const nodeData = selectedNode.data || {};

  const handleChange = (field, value) => {
    if (field === 'bezeichnung') {
      updateNode(selectedNode.id, { bezeichnung: value });
    } else {
      updateNode(selectedNode.id, { data: { ...nodeData, [field]: value } });
    }
  };

  const handleDelete = () => {
    if (window.confirm(`"${selectedNode.bezeichnung}" wirklich löschen?`)) {
      deleteNode(selectedNode.id);
    }
  };

  // Produktliste basierend auf Hersteller und Typ
  const getProductList = () => {
    const hersteller = nodeData.hersteller;
    if (!hersteller || !products[hersteller]) return [];
    
    const herstellerProducts = products[hersteller];
    const type = selectedNode.type;
    
    // Mapping von Komponententypen zu Produktkategorien
    const typeMapping = {
      sls: 'sls',
      ls: 'ls',
      rcd: 'rcd',
      rcbo: 'rcbo',
      schuetz: 'schuetz',
      motorschutz: 'motorschutz',
      klemme: ['durchgangsklemme', 'etagenklemme', 'verbindungsklemme'],
      homematicAktor: ['wired', 'funk'],
      dimmer: 'dimmer',
      stromstoss: 'stromstoss',
      treppenlicht: 'treppenlicht',
      logo: 'logo',
      meldeleuchte: 'm22',
      taster: 'm22',
      sammelschiene: 'sammelschiene',
      verteiler: 'verteiler',
      smartMeter: 'smartmeter'
    };

    const productKey = typeMapping[type];
    if (!productKey) return [];

    if (Array.isArray(productKey)) {
      return productKey.flatMap(key => herstellerProducts[key] || []);
    }
    
    if (Array.isArray(herstellerProducts)) {
      return herstellerProducts;
    }
    
    return herstellerProducts[productKey] || [];
  };

  const productList = getProductList();

  const renderField = (field) => {
    switch (field) {
      case 'bezeichnung':
        return (
          <div key={field} className="space-y-2">
            <Label>Bezeichnung</Label>
            <Input
              value={selectedNode.bezeichnung || ''}
              onChange={(e) => handleChange('bezeichnung', e.target.value)}
              placeholder="Bezeichnung eingeben"
            />
          </div>
        );
      
      case 'bmk':
        return (
          <div key={field} className="space-y-2">
            <Label>BMK</Label>
            <Input
              value={nodeData.bmk || ''}
              onChange={(e) => handleChange('bmk', e.target.value)}
              placeholder="auto"
              className={!nodeData.bmk ? 'text-gray-400 placeholder:text-gray-400' : ''}
            />
          </div>
        );
      
      case 'hersteller':
        return (
          <div key={field} className="space-y-2">
            <Label>Hersteller</Label>
            <Select
              value={nodeData.hersteller || ''}
              onValueChange={(value) => handleChange('hersteller', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Hersteller wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hager">Hager</SelectItem>
                <SelectItem value="dehn">DEHN</SelectItem>
                <SelectItem value="obo">OBO Bettermann</SelectItem>
                <SelectItem value="phoenix">Phoenix Contact</SelectItem>
                <SelectItem value="schrack">Schrack Technik</SelectItem>
                <SelectItem value="siemens">Siemens</SelectItem>
                <SelectItem value="eaton">Eaton</SelectItem>
                <SelectItem value="eltako">Eltako</SelectItem>
                <SelectItem value="wago">WAGO</SelectItem>
                <SelectItem value="homematic">Homematic IP / eq3</SelectItem>
                <SelectItem value="fronius">Fronius</SelectItem>
                <SelectItem value="shelly">Shelly</SelectItem>
                <SelectItem value="kostal">KOSTAL</SelectItem>
                <SelectItem value="e3dc">E3/DC</SelectItem>
                <SelectItem value="janitza">Janitza</SelectItem>
                <SelectItem value="spelsberg">Spelsberg</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'artikelnummer':
        return (
          <div key={field} className="space-y-2">
            <Label>Artikelnummer</Label>
            {productList.length > 0 ? (
              <Select
                value={nodeData.artikelnummer || ''}
                onValueChange={(value) => {
                  const product = productList.find(p => p.id === value);
                  if (product) {
                    // Auto-fill andere Felder basierend auf dem Produkt
                    const updates = { artikelnummer: value };
                    if (product.nennstrom) updates.nennstrom = product.nennstrom;
                    if (product.charakteristik) updates.charakteristik = product.charakteristik;
                    if (product.pole) updates.pole = product.pole;
                    if (product.fehlerstrom) updates.fehlerstrom = product.fehlerstrom;
                    if (product.typ) updates.typ = product.typ;
                    if (product.spulenspannung) updates.spulenspannung = product.spulenspannung;
                    if (product.kontakte) updates.kontakte = product.kontakte;
                    if (product.einstellbereich) updates.einstellbereich = product.einstellbereich;
                    if (product.querschnitt) updates.querschnitt = product.querschnitt;
                    if (product.ebenen) updates.ebenen = product.ebenen;
                    if (product.kanaele) updates.kanaele = product.kanaele;
                    if (product.leistung) updates.leistung = product.leistung;
                    if (product.zeitbereich) updates.zeitbereich = product.zeitbereich;
                    updateNode(selectedNode.id, { data: { ...nodeData, ...updates } });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Produkt wählen" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {productList.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      <span className="font-mono text-xs mr-2">{product.id}</span>
                      <span className="text-gray-600">{product.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={nodeData.artikelnummer || ''}
                onChange={(e) => handleChange('artikelnummer', e.target.value)}
                placeholder="Artikelnummer eingeben"
              />
            )}
          </div>
        );

      case 'kabeltyp':
        return (
          <div key={field} className="space-y-2">
            <Label>Kabeltyp</Label>
            <Select
              value={nodeData.kabeltyp || ''}
              onValueChange={(value) => handleChange('kabeltyp', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Kabeltyp wählen" />
              </SelectTrigger>
              <SelectContent>
                {cableTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'querschnitt':
        return (
          <div key={field} className="space-y-2">
            <Label>Querschnitt (mm²)</Label>
            <Select
              value={nodeData.querschnitt || ''}
              onValueChange={(value) => handleChange('querschnitt', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Querschnitt wählen" />
              </SelectTrigger>
              <SelectContent>
                {crossSections.map((cs) => (
                  <SelectItem key={cs} value={cs}>
                    {cs} mm²
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'nennstrom':
        return (
          <div key={field} className="space-y-2">
            <Label>Nennstrom (A)</Label>
            <Select
              value={nodeData.nennstrom || ''}
              onValueChange={(value) => handleChange('nennstrom', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Nennstrom wählen" />
              </SelectTrigger>
              <SelectContent>
                {ratedCurrents.map((current) => (
                  <SelectItem key={current} value={current}>
                    {current} A
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'fehlerstrom':
        return (
          <div key={field} className="space-y-2">
            <Label>Fehlerstrom (mA)</Label>
            <Select
              value={nodeData.fehlerstrom || ''}
              onValueChange={(value) => handleChange('fehlerstrom', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Fehlerstrom wählen" />
              </SelectTrigger>
              <SelectContent>
                {faultCurrents.map((fc) => (
                  <SelectItem key={fc.value} value={fc.value}>
                    {fc.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'charakteristik':
        return (
          <div key={field} className="space-y-2">
            <Label>Auslösecharakteristik</Label>
            <Select
              value={nodeData.charakteristik || ''}
              onValueChange={(value) => handleChange('charakteristik', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Charakteristik wählen" />
              </SelectTrigger>
              <SelectContent>
                {characteristics.map((char) => (
                  <SelectItem key={char.value} value={char.value}>
                    {char.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'pole':
        return (
          <div key={field} className="space-y-2">
            <Label>Polzahl</Label>
            <Select
              value={nodeData.pole || ''}
              onValueChange={(value) => handleChange('pole', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Polzahl wählen" />
              </SelectTrigger>
              <SelectContent>
                {poles.map((pole) => (
                  <SelectItem key={pole.value} value={pole.value}>
                    {pole.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'phase':
        // Bei 1-poligen LS/RCBO oder 1-phasigen ÜSS anzeigen
        const poleValue = nodeData.pole || '1';
        const ussPhasenValue = nodeData.ussPhasen;
        const showPhase = poleValue === '1' || ussPhasenValue === '1P';
        if (!showPhase && selectedNode.type !== 'ueberspannungsschutz') return null;
        if (selectedNode.type === 'ueberspannungsschutz' && ussPhasenValue !== '1P') return null;
        return (
          <div key={field} className="space-y-2">
            <Label>Phase</Label>
            <Select
              value={nodeData.phase || 'auto'}
              onValueChange={(value) => handleChange('phase', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Phase wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatisch</SelectItem>
                <SelectItem value="L1">L1</SelectItem>
                <SelectItem value="L2">L2</SelectItem>
                <SelectItem value="L3">L3</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {selectedNode.type === 'ueberspannungsschutz' 
                ? 'Welche Phase der 1-phasige ÜSS absichert'
                : 'Standardmäßig automatische Phasen-Rotation (L1→L2→L3)'}
            </p>
          </div>
        );

      case 'teilungseinheiten':
        return (
          <div key={field} className="space-y-2">
            <Label>Teilungseinheiten (TE)</Label>
            <Select
              value={nodeData.teilungseinheiten || '1'}
              onValueChange={(value) => handleChange('teilungseinheiten', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="TE wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 TE</SelectItem>
                <SelectItem value="2">2 TE</SelectItem>
                <SelectItem value="3">3 TE</SelectItem>
                <SelectItem value="4">4 TE</SelectItem>
                <SelectItem value="6">6 TE</SelectItem>
                <SelectItem value="8">8 TE</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'verbraucher':
        return (
          <div key={field} className="space-y-2">
            <Label>Verbraucher</Label>
            <Input
              value={nodeData.verbraucher || ''}
              onChange={(e) => handleChange('verbraucher', e.target.value)}
              placeholder="z.B. Herd, Spülmaschine..."
            />
          </div>
        );

      case 'anzahlAdern':
        return (
          <div key={field} className="space-y-2">
            <Label>Anzahl Adern</Label>
            <Select
              value={String(nodeData.anzahlAdern || '3')}
              onValueChange={(value) => {
                const count = parseInt(value, 10);
                // Automatisch Adern-Array erstellen
                const adern = [];
                for (let i = 1; i <= count; i++) {
                  if (i < count - 1) {
                    // Phase-Adern
                    const colors = ['br: L1', 'sw: L2', 'gr: L3'];
                    adern.push({ nr: i, typ: 'L', bezeichnung: colors[(i-1) % 3] || `L${i}`, zuordnung: 'auto' });
                  } else if (i === count - 1) {
                    // N-Ader
                    adern.push({ nr: i, typ: 'N', bezeichnung: 'N', zuordnung: 'auto' });
                  } else {
                    // PE-Ader
                    adern.push({ nr: i, typ: 'PE', bezeichnung: 'PE', zuordnung: 'auto' });
                  }
                }
                updateNode(selectedNode.id, { data: { ...nodeData, anzahlAdern: count, adern } });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Anzahl Adern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2-adrig</SelectItem>
                <SelectItem value="3">3-adrig</SelectItem>
                <SelectItem value="4">4-adrig</SelectItem>
                <SelectItem value="5">5-adrig</SelectItem>
                <SelectItem value="7">7-adrig</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'klemmleiste':
        return (
          <div key={field} className="space-y-2">
            <Label>Klemmleiste</Label>
            <Input
              value={nodeData.klemmleiste || 'X1'}
              onChange={(e) => handleChange('klemmleiste', e.target.value)}
              placeholder="z.B. X1"
            />
          </div>
        );

      case 'leitungsnr':
        return (
          <div key={field} className="space-y-2">
            <Label>Leitungsnummer</Label>
            <Input
              value={nodeData.leitungsnr || ''}
              onChange={(e) => handleChange('leitungsnr', e.target.value)}
              placeholder="z.B. 1"
            />
          </div>
        );

      case 'netzform':
        const currentNetzform = nodeData.netzform || 'TN-C-S';
        const currentPhasen = nodeData.phasenanzahl || '3';
        const leiterConfig = getLeiterConfig(currentNetzform, currentPhasen);
        
        return (
          <div key={field} className="space-y-3">
            <Label className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Netzform
            </Label>
            <Select
              value={currentNetzform}
              onValueChange={(value) => {
                // Beide Werte in einem einzigen Update zusammenfassen
                const newConfig = getLeiterConfig(value, currentPhasen);
                updateNode(selectedNode.id, { 
                  data: { 
                    ...nodeData, 
                    netzform: value,
                    aderzahl: newConfig.aderzahl 
                  } 
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Netzform wählen" />
              </SelectTrigger>
              <SelectContent>
                {gridTypes.map((grid) => (
                  <SelectItem key={grid.value} value={grid.value}>
                    <div className="flex flex-col">
                      <span>{grid.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Kompakte Info zur gewählten Netzform */}
            <div className="p-2 bg-gray-50 rounded text-xs text-gray-600">
              <span className="font-medium">{leiterConfig.kabelbezeichnung}</span>
              <span className="text-gray-400 ml-2">({leiterConfig.zuleitung.join(', ')})</span>
            </div>
          </div>
        );

      case 'phasenanzahl':
        const netzformForPhasen = nodeData.netzform || 'TN-C-S';
        const currentPhasenanzahl = nodeData.phasenanzahl || '3';
        const leiterConfigPhasen = getLeiterConfig(netzformForPhasen, currentPhasenanzahl);
        
        return (
          <div key={field} className="space-y-2">
            <Label>Phasenanzahl</Label>
            <Select
              value={currentPhasenanzahl}
              onValueChange={(value) => {
                // Beide Werte in einem einzigen Update zusammenfassen
                const newConfig = getLeiterConfig(netzformForPhasen, value);
                updateNode(selectedNode.id, { 
                  data: { 
                    ...nodeData, 
                    phasenanzahl: value,
                    aderzahl: newConfig.aderzahl 
                  } 
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Phasenanzahl wählen" />
              </SelectTrigger>
              <SelectContent>
                {phaseCount.map((phase) => (
                  <SelectItem key={phase.value} value={phase.value}>
                    {phase.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Leiter in Zuleitung: {leiterConfigPhasen.zuleitung.join(', ')}
            </p>
          </div>
        );

      case 'einspeisungTyp':
        return (
          <div key={field} className="space-y-2">
            <Label>Einspeisung an</Label>
            <Select
              value={nodeData.einspeisungTyp || 'sammelschiene_adapter'}
              onValueChange={(value) => handleChange('einspeisungTyp', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Einspeisungsart wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sammelschiene_adapter">Sammelschiene mit Einspeiseadapter</SelectItem>
                <SelectItem value="sammelschiene_klemmen">Sammelschiene mit Klemmen</SelectItem>
                <SelectItem value="hauptleitungsklemme">Hauptleitungsklemme</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Art der Einspeisung an der Klemme X0</p>
          </div>
        );

      case 'typ':
        if (selectedNode.type === 'zaehler') {
          return (
            <div key={field} className="space-y-2">
              <Label>Zählertyp</Label>
              <Select
                value={nodeData.typ || ''}
                onValueChange={(value) => handleChange('typ', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Zählertyp wählen" />
                </SelectTrigger>
                <SelectContent>
                  {meterArten.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }
        if (selectedNode.type === 'klemme') {
          return (
            <div key={field} className="space-y-2">
              <Label>Klemmentyp</Label>
              <Select
                value={nodeData.typ || ''}
                onValueChange={(value) => handleChange('typ', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Klemmentyp wählen" />
                </SelectTrigger>
                <SelectContent>
                  {terminalTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }
        if (selectedNode.type === 'rcd' || selectedNode.type === 'rcbo') {
          return (
            <div key={field} className="space-y-2">
              <Label>RCD-Typ</Label>
              <Select
                value={nodeData.typ || ''}
                onValueChange={(value) => handleChange('typ', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="RCD-Typ wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AC">Typ AC</SelectItem>
                  <SelectItem value="A">Typ A</SelectItem>
                  <SelectItem value="F">Typ F</SelectItem>
                  <SelectItem value="B">Typ B</SelectItem>
                  <SelectItem value="B+">Typ B+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          );
        }
        return (
          <div key={field} className="space-y-2">
            <Label>Typ</Label>
            <Input
              value={nodeData.typ || ''}
              onChange={(e) => handleChange('typ', e.target.value)}
              placeholder="Typ eingeben"
            />
          </div>
        );

      case 'laenge':
        return (
          <div key={field} className="space-y-2">
            <Label>Länge (m)</Label>
            <Input
              type="number"
              value={nodeData.laenge || ''}
              onChange={(e) => handleChange('laenge', e.target.value)}
              placeholder="Länge in Metern"
            />
          </div>
        );

      case 'vorabsicherung':
        return (
          <div key={field} className="space-y-2">
            <Label>Vorabsicherung</Label>
            <Input
              value={nodeData.vorabsicherung || ''}
              onChange={(e) => handleChange('vorabsicherung', e.target.value)}
              placeholder="z.B. 63A"
            />
          </div>
        );

      case 'bauform':
        return (
          <div key={field} className="space-y-2">
            <Label>Bauform</Label>
            <Select
              value={nodeData.bauform || ''}
              onValueChange={(value) => handleChange('bauform', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Bauform wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Sammelschiene">Sammelschiene</SelectItem>
                <SelectItem value="Schraubanschluss">Schraubanschluss</SelectItem>
                <SelectItem value="Steckanschluss">Steckanschluss</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'messart':
        return (
          <div key={field} className="space-y-2">
            <Label>Messart</Label>
            <Select
              value={nodeData.messart || 'direkt'}
              onValueChange={(value) => handleChange('messart', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Messart wählen" />
              </SelectTrigger>
              <SelectContent>
                {meterMessarten.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {nodeData.messart === 'wandler' && (
              <p className="text-xs text-amber-600">Wandlermessung: Stromwandler werden separat gezeichnet</p>
            )}
          </div>
        );

      case 'montageart':
        return (
          <div key={field} className="space-y-2">
            <Label>Montageart</Label>
            <Select
              value={nodeData.montageart || 'ehz'}
              onValueChange={(value) => handleChange('montageart', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Montageart wählen" />
              </SelectTrigger>
              <SelectContent>
                {meterMontagearten.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'zaehlerart':
        return (
          <div key={field} className="space-y-2">
            <Label>Zählerart</Label>
            <Select
              value={nodeData.zaehlerart || 'drehstrom'}
              onValueChange={(value) => handleChange('zaehlerart', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Zählerart wählen" />
              </SelectTrigger>
              <SelectContent>
                {meterArten.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'verdrahtung':
        return (
          <div key={field} className="space-y-2">
            <Label>Zählerverdrahtung</Label>
            <Select
              value={nodeData.verdrahtung || '16'}
              onValueChange={(value) => handleChange('verdrahtung', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Verdrahtung wählen" />
              </SelectTrigger>
              <SelectContent>
                {meterVerdrahtung.map((v) => (
                  <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 's0Schnittstelle':
        return (
          <div key={field} className="flex items-center space-x-2 py-2">
            <Checkbox
              id="s0"
              checked={nodeData.s0Schnittstelle || false}
              onCheckedChange={(checked) => handleChange('s0Schnittstelle', checked)}
            />
            <Label htmlFor="s0" className="cursor-pointer">S0-Schnittstelle (Impulsausgang)</Label>
          </div>
        );

      case 'tarifumschaltung':
        return (
          <div key={field} className="flex items-center space-x-2 py-2">
            <Checkbox
              id="tarif"
              checked={nodeData.tarifumschaltung || false}
              onCheckedChange={(checked) => handleChange('tarifumschaltung', checked)}
            />
            <Label htmlFor="tarif" className="cursor-pointer">Tarifumschaltung (Steuereingang)</Label>
          </div>
        );

      case 'spulenspannung':
        return (
          <div key={field} className="space-y-2">
            <Label>Spulenspannung</Label>
            <Select
              value={nodeData.spulenspannung || ''}
              onValueChange={(value) => handleChange('spulenspannung', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Spulenspannung wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24VDC">24V DC</SelectItem>
                <SelectItem value="24VAC">24V AC</SelectItem>
                <SelectItem value="230VAC">230V AC</SelectItem>
                <SelectItem value="400VAC">400V AC</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'kontakte':
        return (
          <div key={field} className="space-y-2">
            <Label>Kontakte</Label>
            <Input
              value={nodeData.kontakte || ''}
              onChange={(e) => handleChange('kontakte', e.target.value)}
              placeholder="z.B. 3NO+1NC"
            />
          </div>
        );

      case 'einstellbereich':
        return (
          <div key={field} className="space-y-2">
            <Label>Einstellbereich (A)</Label>
            <Input
              value={nodeData.einstellbereich || ''}
              onChange={(e) => handleChange('einstellbereich', e.target.value)}
              placeholder="z.B. 2.5-4"
            />
          </div>
        );

      case 'aderzahl':
        return (
          <div key={field} className="space-y-2">
            <Label>Aderzahl</Label>
            <Select
              value={nodeData.aderzahl || ''}
              onValueChange={(value) => handleChange('aderzahl', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Aderzahl wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2-adrig</SelectItem>
                <SelectItem value="3">3-adrig</SelectItem>
                <SelectItem value="4">4-adrig</SelectItem>
                <SelectItem value="5">5-adrig</SelectItem>
                <SelectItem value="7">7-adrig</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'ebenen':
        return (
          <div key={field} className="space-y-2">
            <Label>Ebenen</Label>
            <Select
              value={nodeData.ebenen || ''}
              onValueChange={(value) => handleChange('ebenen', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Ebenen wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 Ebene</SelectItem>
                <SelectItem value="2">2 Ebenen</SelectItem>
                <SelectItem value="3">3 Ebenen</SelectItem>
                <SelectItem value="4">4 Ebenen</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'kanaele':
        return (
          <div key={field} className="space-y-2">
            <Label>Kanäle</Label>
            <Input
              type="number"
              value={nodeData.kanaele || ''}
              onChange={(e) => handleChange('kanaele', e.target.value)}
              placeholder="Anzahl Kanäle"
            />
          </div>
        );

      case 'leistung':
        return (
          <div key={field} className="space-y-2">
            <Label>Leistung (W)</Label>
            <Input
              type="number"
              value={nodeData.leistung || ''}
              onChange={(e) => handleChange('leistung', e.target.value)}
              placeholder="Leistung in Watt"
            />
          </div>
        );

      case 'zeitbereich':
        return (
          <div key={field} className="space-y-2">
            <Label>Zeitbereich</Label>
            <Input
              value={nodeData.zeitbereich || ''}
              onChange={(e) => handleChange('zeitbereich', e.target.value)}
              placeholder="z.B. 0.5-20min"
            />
          </div>
        );

      // ÜSS-spezifische Felder
      case 'ussTyp':
        return (
          <div key={field} className="space-y-2">
            <Label>Überspannungsschutz-Typ</Label>
            <Select
              value={nodeData.ussTyp || 'Typ1+2'}
              onValueChange={(value) => handleChange('ussTyp', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Typ wählen" />
              </SelectTrigger>
              <SelectContent>
                {ussTypes.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'ussBauform':
        return (
          <div key={field} className="space-y-2">
            <Label>Bauform</Label>
            <Select
              value={nodeData.ussBauform || 'Sammelschiene'}
              onValueChange={(value) => handleChange('ussBauform', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Bauform wählen" />
              </SelectTrigger>
              <SelectContent>
                {ussBauformen.map((b) => (
                  <SelectItem key={b.value} value={b.value}>
                    {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'ussPhasen':
        return (
          <div key={field} className="space-y-2">
            <Label>Phasenanbindung</Label>
            <Select
              value={nodeData.ussPhasen || '3P+N'}
              onValueChange={(value) => handleChange('ussPhasen', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Phasen wählen" />
              </SelectTrigger>
              <SelectContent>
                {ussPhasen.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {nodeData.ussPhasen === '1P' 
                ? 'Bei 1-phasig: Wählen Sie unten die Phase (L1, L2 oder L3)'
                : 'ÜSS hat 2 Erdungsanschlüsse: PE zum Verteiler + PAS (Potenzialausgleichsschiene)'}
            </p>
          </div>
        );

      default:
        return (
          <div key={field} className="space-y-2">
            <Label>{field.charAt(0).toUpperCase() + field.slice(1)}</Label>
            <Input
              value={nodeData[field] || ''}
              onChange={(e) => handleChange(field, e.target.value)}
              placeholder={`${field} eingeben`}
            />
          </div>
        );
    }
  };

  return (
    <div className="h-full overflow-auto bg-gray-50 p-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {componentType?.name || 'Komponente'}
                <Badge variant="outline" style={{ borderColor: componentType?.color, color: componentType?.color }}>
                  {selectedNode.type}
                </Badge>
              </CardTitle>
              <CardDescription>ID: {selectedNode.id}</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Copy className="w-4 h-4" />
              </Button>
              <Button variant="destructive" size="sm" onClick={handleDelete}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Bezeichnung immer zuerst */}
            {renderField('bezeichnung')}
            
            <Separator />

            {/* Typ-spezifische Felder */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {componentType?.fields
                ?.filter(f => f !== 'bezeichnung')
                .map(field => renderField(field))}
            </div>

            {/* Adern-Editor für Kabel */}
            {selectedNode.type === 'kabel' && nodeData.adern && nodeData.adern.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Adern-Konfiguration</Label>
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-2 py-1 text-left w-12">#</th>
                          <th className="px-2 py-1 text-left w-20">Typ</th>
                          <th className="px-2 py-1 text-left">Bezeichnung</th>
                          <th className="px-2 py-1 text-left w-32">Zuordnung</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nodeData.adern.map((ader, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-2 py-1 text-gray-500">{ader.nr}</td>
                            <td className="px-2 py-1">
                              <Select
                                value={ader.typ || 'L'}
                                onValueChange={(value) => {
                                  const newAdern = [...nodeData.adern];
                                  newAdern[idx] = { ...newAdern[idx], typ: value };
                                  updateNode(selectedNode.id, { data: { ...nodeData, adern: newAdern } });
                                }}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="L">L (Phase)</SelectItem>
                                  <SelectItem value="N">N (Neutral)</SelectItem>
                                  <SelectItem value="PE">PE (Erde)</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                className="h-7 text-xs"
                                value={ader.bezeichnung || ''}
                                onChange={(e) => {
                                  const newAdern = [...nodeData.adern];
                                  newAdern[idx] = { ...newAdern[idx], bezeichnung: e.target.value };
                                  updateNode(selectedNode.id, { data: { ...nodeData, adern: newAdern } });
                                }}
                                placeholder="z.B. br: L1"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Dialog open={zuordnungDialog.open && zuordnungDialog.aderIdx === idx} onOpenChange={(open) => setZuordnungDialog({ open, aderIdx: open ? idx : null })}>
                                <DialogTrigger asChild>
                                  <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start">
                                    <Link2 className="w-3 h-3 mr-1" />
                                    {ader.zuordnung && ader.zuordnung !== 'auto' 
                                      ? <span className="truncate">{ader.zuordnung}</span>
                                      : <span className="text-gray-400">Zuordnen...</span>
                                    }
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-md max-h-[70vh] overflow-hidden flex flex-col">
                                  <DialogHeader>
                                    <DialogTitle>Ader {ader.nr} zuordnen</DialogTitle>
                                  </DialogHeader>
                                  <div className="flex-1 overflow-auto">
                                    <div className="space-y-1">
                                      <Button
                                        variant="ghost"
                                        className="w-full justify-start text-left h-auto py-2"
                                        onClick={() => handleZuordnung(idx, null)}
                                      >
                                        <span className="text-gray-500">Automatisch (keine Zuordnung)</span>
                                      </Button>
                                      {getZuordnungTargets().map((target, tIdx) => (
                                        <Button
                                          key={tIdx}
                                          variant="ghost"
                                          className="w-full justify-start text-left h-auto py-2"
                                          onClick={() => handleZuordnung(idx, target)}
                                        >
                                          <ChevronRight className="w-4 h-4 mr-2 text-gray-400" />
                                          <span className="text-sm">{target.label}</span>
                                        </Button>
                                      ))}
                                      {getZuordnungTargets().length === 0 && (
                                        <p className="text-sm text-gray-500 p-2">
                                          Keine Bauteile mit Ein/Ausgängen vorhanden. 
                                          Fügen Sie zuerst Relais, Aktoren, Dimmer etc. hinzu.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500">Klicken Sie auf &quot;Zuordnen&quot; um die Ader mit einem Bauteil-Ein/Ausgang zu verbinden.</p>
                </div>
              </>
            )}

            {/* Notizen */}
            <Separator />
            <div className="space-y-2">
              <Label>Notizen</Label>
              <Textarea
                value={nodeData.notizen || ''}
                onChange={(e) => handleChange('notizen', e.target.value)}
                placeholder="Zusätzliche Informationen..."
                rows={3}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComponentEditor;
